import { Loader2, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

const LoadingScreen = () => {
  const [loadingTime, setLoadingTime] = useState(0);
  const [isStuckLoading, setIsStuckLoading] = useState(false);
  
  // Track loading time and show appropriate messages
  useEffect(() => {
    const startTime = Date.now();
    
    // Mark loading start time for recovery mechanism
    if (import.meta.env.PROD) {
      try {
        sessionStorage.setItem('loadingStartTime', startTime.toString());
        sessionStorage.setItem('loadingScreenShown', 'true');
      } catch (e) {
        // Ignore storage errors
      }
    }
    
    // Add a class for the recovery mechanism to detect
    const addLoadingIndicatorClass = () => {
      const indicator = document.createElement('div');
      indicator.className = 'loading-indicator';
      indicator.style.display = 'none';
      document.body.appendChild(indicator);
      
      return () => {
        if (indicator && indicator.parentNode) {
          indicator.parentNode.removeChild(indicator);
        }
      };
    };
    
    const removeIndicator = addLoadingIndicatorClass();
    
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setLoadingTime(elapsed);
      
      // After 15 seconds, consider loading "stuck"
      if (elapsed > 15 && !isStuckLoading) {
        setIsStuckLoading(true);
      }
    }, 1000);
    
    return () => {
      clearInterval(timer);
      removeIndicator();
      
      // Clear loading state
      if (import.meta.env.PROD) {
        try {
          sessionStorage.removeItem('loadingStartTime');
          sessionStorage.removeItem('loadingScreenShown');
        } catch (e) {
          // Ignore storage errors
        }
      }
    };
  }, []);
  
  // Handle manual refresh for stuck loading
  const handleForceRefresh = () => {
    // Clear any cached auth data that might be causing issues
    try {
      sessionStorage.removeItem('userRole');
      // Don't clear the main auth token as that would log the user out
    } catch (e) {
      // Ignore storage errors
    }
    
    // Force reload the page
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
      <div className="text-center max-w-md mx-auto px-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto" />
        <p className="mt-4 text-lg text-gray-700">Chargement en cours...</p>
        
        {loadingTime > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            Temps écoulé: {loadingTime} seconde{loadingTime > 1 ? 's' : ''}
          </p>
        )}
        
        {loadingTime >= 5 && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md text-sm text-left">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Chargement lent détecté</p>
                <p className="mt-1 text-amber-700">Si la page reste bloquée, essayez:</p>
                <ul className="list-disc list-inside mt-2 text-amber-700 space-y-1">
                  <li>Vider le cache et les cookies du navigateur</li>
                  <li>Vous déconnecter et vous reconnecter</li>
                  <li>Utiliser une navigation privée</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {isStuckLoading && (
          <div className="mt-4">
            <button 
              onClick={handleForceRefresh}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Forcer le rechargement
            </button>
            <p className="mt-2 text-xs text-gray-500">
              En cas de problème persistant, contactez l'administrateur
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingScreen;